import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Headphones, Mic, BookOpen, PenTool, GraduationCap, FileText, Users, Package, ClipboardCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const Footer = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isExamMode = location.pathname.includes('/exam/') && !location.pathname.includes('/setup');
  const isAuthPage = location.pathname === '/login' || location.pathname === '/auth/callback';
  // v6.32.232 — initial state is null so a fetch failure (CORS, cache miss,
  // network blip) displays "…" instead of a misleading stale "v2.0.0".
  // Pre-232 the hardcoded '2.0.0' fallback made it look like Production
  // was running a pre-MOSAIC build whenever the fetch silently failed.
  const [versionInfo, setVersionInfo] = React.useState(null);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/version`).then(r => r.json()).then(d => setVersionInfo(d)).catch(() => {});
  }, []);
  
  if (isExamMode || isAuthPage) return null;
  
  // ERMA reviewers have their own contained layout
  if (user?.role === 'erma_reviewer') return null;
  
  return (
    <footer className="app-footer no-print">
      <div className="app-footer-content">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/mosaic-mascot.png" alt="MOSAIC Mascot" className="h-16 w-16 object-contain" />
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                MOSAIC
              </h3>
            </div>
            <p className="text-gray-400 text-sm">
              <strong>M</strong>ultilingual <strong>O</strong>ral & Written <strong>S</strong>tudent <strong>A</strong>ssessment for <strong>I</strong>nstructional <strong>C</strong>larity. Preparing NYC students for English language proficiency assessment across all four language domains.
            </p>
            <p className="text-xs text-gray-300 mt-2">"Every piece matters."</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Modalities</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-center gap-2"><Headphones size={14} /> Listening</li>
              <li className="flex items-center gap-2"><Mic size={14} /> Speaking</li>
              <li className="flex items-center gap-2"><BookOpen size={14} /> Reading</li>
              <li className="flex items-center gap-2"><PenTool size={14} /> Writing</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Programs</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <Link to="/features" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-program-assessments">
                  <ClipboardCheck size={14} /> Language Assessment Platform
                </Link>
              </li>
              <li>
                <Link to="/features" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-program-toolkit">
                  <PenTool size={14} /> Teacher Toolkit
                </Link>
              </li>
              <li>
                <Link to="/stability-kits" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-program-stability-kits">
                  <Package size={14} /> Student Stability Kits (K-8)
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-center gap-2"><GraduationCap size={14} /> Grade K-12 Coverage</li>
              <li className="flex items-center gap-2"><FileText size={14} /> Print & Digital Modes</li>
              <li className="flex items-center gap-2"><Users size={14} /> Teacher Answer Keys</li>
            </ul>
            <h4 className="font-semibold mt-6 mb-3">Contact</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="mailto:orders@mosaicassessmentco.com" className="hover:text-white transition-colors" data-testid="footer-orders">Orders</a></li>
              <li><a href="mailto:compliance@mosaicassessmentco.com" className="hover:text-white transition-colors" data-testid="footer-compliance">District IT / Privacy</a></li>
              <li><a href="mailto:support@mosaicassessmentco.com" className="hover:text-white transition-colors" data-testid="footer-support">Support</a></li>
            </ul>
            <h4 className="font-semibold mt-6 mb-3">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/join-beta" className="hover:text-white transition-colors" data-testid="footer-join-beta">Join the Waitlist</Link></li>
              <li><Link to="/request-demo" className="hover:text-white transition-colors" data-testid="footer-request-demo">Request a Demo</Link></li>
              <li><Link to="/try-demo" className="hover:text-white transition-colors" data-testid="footer-try-demo">Try Free Demo</Link></li>
              <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/subprocessors" className="hover:text-white transition-colors" data-testid="footer-subprocessors">Subprocessors</Link></li>
              <li><Link to="/about/proficiency-fidelity" className="hover:text-white transition-colors" data-testid="footer-proficiency-fidelity">Assessment Quality Report</Link></li>
              <li><a href="/docs/MOSAIC_AI_Transparency_OnePager.pdf" className="hover:text-white transition-colors" data-testid="footer-ai-transparency">Automated Processing Transparency</a></li>
            </ul>
            <p className="text-xs text-gray-400 mt-6">NYC Vendor Code: <span className="text-gray-200 font-mono" data-testid="footer-nyc-vendor-code">LER599869</span></p>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-300 text-sm">
          <p>&copy; {new Date().getFullYear()} MOSAIC. All rights reserved.</p>
          <p className="text-xs text-gray-300 mt-1">Created by <a href="https://mosaicassessmentco.com" target="_blank" rel="noopener noreferrer" className="underline text-gray-100 hover:text-white transition-colors">Lerno Co.</a></p>
          <p className="text-[10px] text-gray-300 mt-2 max-w-xl mx-auto leading-relaxed">WIDA&reg; is a registered trademark of the Board of Regents of the University of Wisconsin System. MOSAIC is not affiliated with, endorsed by, or sponsored by WIDA.</p>
          <span
            data-testid="version-badge"
            className="inline-block mt-3 px-3 py-1 rounded-full text-[10px] font-mono tracking-wider"
            style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f766e 100%)', color: '#94a3b8' }}
          >
            {versionInfo?.version ? `v${versionInfo.version}` : 'v…'}
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
